import React from 'react';
import moment from 'moment';
import { isFreeCashbackCoupon } from '../../lib';
import {
  AutocompleteInput,
  List,
  Filter,
  Datagrid,
  NumberField,
  DateField,
  SelectField,
  SelectInput,
  ReferenceInput,
  ReferenceField,
  TextField,
  useRecordContext,
  useNotify,
  Button,
  FunctionField,
  EditButton,
  Labeled,
  useTranslate,
} from 'react-admin';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/GetApp';
import { downloadBlob, formatPoints, filterToQuery } from '../../utils';
import { BillingStatus } from '../../models'

const removeMarginSx = {
  marginTop: 0
};

const filesListSx = {
  display: "flex",
  flexDirection: "column"
};

const PaymentEditButton = () => {
  const record = useRecordContext();
  if (!record) { return null; }
  return (
    <EditButton resource="billings-payment" label='Pagar' record={record} disabled={record.status === BillingStatus.closed} />
  )
}

const DownloadFilesButton = (props) => {
  const record = useRecordContext(props);
  if (!record) { return null; }
  const notify = useNotify();

  const handleDownloadFile = async () => {
    try {
      await downloadBlob(props.path, `invoice-${props.index + 1}-${record.id}`);
    } catch (error) {
      notify(error.message || "Erro ao baixar arquivo", { type: "warning" });
    }
  };

  return (
    <Button label={props.label} startIcon={<DownloadIcon />} onClick={handleDownloadFile} />
  );
}

const CouponsPanel = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <List
      title=" "
      resource="coupons"
      basePath="coupons"
      sort={{ field: 'redeemedAt', order: 'ASC' }}
      filter={{ billingId: record.id }}
      pagination={false}
      perPage={0}
      actions={false}
      bulkActionButtons={false}
      disableSyncWithLocation
    >
      <Datagrid bulkActionButtons={false}>
        <FunctionField label="Produto" textAlign="center" render={record => isFreeCashbackCoupon(record) ? "Cashback" : record.product.name || 'Não identificado'} />
        <FunctionField
          source="value"
          label="Pontos"
          textAlign="center"
          render={record => formatPoints(+record?.value)}
        />
        <NumberField
          source="partnerValue"
          label="Valor"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: "currency",
            currency: "BRL",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
        <DateField source="redeemedAt" label="Data do resgate" textAlign="center" showTime locales="pt-BR" />
        <DateField source="createdAt" label="Data de geração" textAlign="center" showTime locales="pt-BR" />
      </Datagrid>
    </List>
  );
};

const FilterList = ({ ...props }) => {
  const translate = useTranslate();
  
  return (
    <Filter {...props}>
      <BetweenDatesInput alwaysOn />
      <SelectInput
        source="status"
        label="Status"
        choices={BillingStatus.BillingStatusChoices}
        emptyText="Todos"
        alwaysOn
      />
      <ReferenceInput
        source="placeId"
        reference="chains/chainId/partners"
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
        perPage={null}
      >
        <AutocompleteInput
          filterToQuery={filterToQuery}
          label="Parceiro"
          style={{ minWidth: 180 }}
          optionText={'name'}
          noOptionsText={translate('ra.input.autocomplete.no_options_text')}
        />
      </ReferenceInput>
    </Filter>
  );
};

const BillingsDatagrid = () => {
  return (
    <Datagrid
      bulkActionButtons={false}
      style={{ marginTop: 40 }}
      expand={<CouponsPanel />}
    >
      <NumberField source="number" label="Número da fatura" textAlign="center" />
      <NumberField
        source="totalValue"
        label="Valor total"
        textAlign="center"
        emptyText="R$ 0,00"
        options={{
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }}
      />
      <DateField source="expiresAt" label="Data de vencimento" textAlign="center" locales="pt-BR" />
      <SelectField
        source="status"
        label="Status"
        textAlign="center"
        choices={BillingStatus.BillingStatusChoices}
      />
      <ReferenceField
        source="placeId"
        reference="chains/chainId/partners"
        label="Parceiro"
        textAlign="center"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <TextField
        source="partnerNote"
        label="Observação do Parceiro"
        textAlign="center"
        emptyText="-"
        sortable={false}
      />
      <FunctionField
        label="Arquivos de NFe"
        textAlign="center"
        sx={filesListSx}
        render={(record) =>
          record.filePath && record.filePath.length ?
            record.filePath.map((path, i) => (
              <DownloadFilesButton label={`PDF ${i + 1}`} path={path} index={i} />
            )) :
            "Sem registro"
        }
      />
      <Labeled label=" " sx={removeMarginSx} >
        <PaymentEditButton />
      </Labeled>
    </Datagrid>
  );
}

export default ({ permissions, ...props }) => (
  <Permission permission={PERMISSIONS.BILLING_PAYMENT}>
    <List
      {...props}
      perPage={25}
      pagination={<ListPagination />}
      exporter={false}
      title="Contas a pagar"
      resource="billings"
      filters={<FilterList {...props} />}
      hasCreate={false}
      filterDefaultValues={{
        from: moment().startOf("day").subtract(1, "month").toISOString(),
        to: moment().endOf("day").toISOString(),
      }}
    >
      <BillingsDatagrid />
    </List>
  </Permission>
);
